import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Search, Megaphone } from 'lucide-react';
import { useSuppliers, useFeaturedSuppliers } from '../data/hooks';
import SupplierCard from '../components/SupplierCard';
import FeaturedSupplierCard from '../components/FeaturedSupplierCard';
import FilterSidebar, { buildEmptyFilters, MobileFilterDrawer } from '../components/FilterSidebar';
import { LoadingState, ErrorState, EmptyState } from '../components/states/AsyncStates';
import { track, EVENTS } from '../lib/analytics';
import SEO from '../components/SEO';
import Img from '../components/Img';
import { EDITORIAL_HEROES } from '../data/heroImages';

const MANUFACTURER_TYPES = ['Direct Manufacturer', 'OEM Factory', 'ODM Factory'];

const FactoryDirectory = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState(buildEmptyFilters());
  const [drawer, setDrawer] = useState(false);
  const [sort, setSort] = useState('trust-desc');

  const { data: allSuppliers, loading, error, refetch } = useSuppliers();
  const { data: featured } = useFeaturedSuppliers();

  const results = useMemo(() => {
    if (!allSuppliers) return [];
    let arr = [...allSuppliers];
    if (query) {
      const q = query.toLowerCase();
      arr = arr.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.tagline || '').toLowerCase().includes(q) ||
        (s.country || '').toLowerCase().includes(q) ||
        (s.specialties || []).some(sp => sp.toLowerCase().includes(q))
      );
    }
    if (filters.manufacturersOnly) arr = arr.filter(s => MANUFACTURER_TYPES.includes(s.type));
    if (filters.countries.length) arr = arr.filter(s => filters.countries.includes(s.country));
    if (filters.supplierTypes.length) arr = arr.filter(s => filters.supplierTypes.includes(s.type));
    if (filters.certifications.length) arr = arr.filter(s => filters.certifications.every(c => (s.certifications || []).includes(c)));
    if (filters.sustainability.length) arr = arr.filter(s => filters.sustainability.some(c => (s.certifications || []).includes(c)));
    if (filters.shipping.length) arr = arr.filter(s => filters.shipping.some(sh => (s.shipping || []).includes(sh)));
    if (filters.categories.length) arr = arr.filter(s => filters.categories.some(c => (s.categories || []).includes(c)));
    arr = arr.filter(s => (s.moqMin ?? 0) <= filters.moqMax);
    arr = arr.filter(s => (s.trustScore ?? 0) >= filters.trustMin);

    if (sort === 'trust-desc') arr.sort((a, b) => (b.trustScore || 0) - (a.trustScore || 0));
    if (sort === 'moq-asc') arr.sort((a, b) => (a.moqMin || 0) - (b.moqMin || 0));
    if (sort === 'response') arr.sort((a, b) => (b.responseRate || 0) - (a.responseRate || 0));
    return arr;
  }, [allSuppliers, query, filters, sort]);

  // Debounced search analytics — fire only when the user pauses typing.
  useEffect(() => {
    if (!query.trim()) return;
    const id = window.setTimeout(() => {
      track(EVENTS.FACTORY_SEARCH, { query_length: query.length, result_count: results.length });
    }, 750);
    return () => window.clearTimeout(id);
  }, [query, results.length]);

  // Filter usage — fire whenever an active filter set changes.
  useEffect(() => {
    const activeCount =
      filters.countries.length + filters.supplierTypes.length +
      filters.certifications.length + filters.sustainability.length +
      filters.shipping.length + filters.categories.length +
      (filters.manufacturersOnly ? 1 : 0);
    if (activeCount === 0) return;
    track(EVENTS.FILTER_APPLIED, {
      surface: 'factory_directory',
      active_count: activeCount,
      manufacturers_only: filters.manufacturersOnly,
      country_count: filters.countries.length,
      certification_count: filters.certifications.length,
      sustainability_count: filters.sustainability.length,
      result_count: results.length,
    });
  }, [filters, results.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="factory-directory-page" className="surface-obsidian">
      <SEO
        title="Factory Directory — Verified Manufacturers Worldwide"
        description="Filter verified factories by country, supplier type, certification, MOQ, and trust score. Manufacturers-only toggle excludes trading companies. 12 sourcing countries indexed."
        keywords="factory directory, verified manufacturers, OEM factory, ODM factory, supplier search, sourcing platform"
      />
      <section className="relative overflow-hidden border-b border-black" style={{ background: '#0B0B0B', color: '#F7F7F5' }}>
        <div className="absolute inset-0">
          <Img src={EDITORIAL_HEROES.factories.src} alt={EDITORIAL_HEROES.factories.alt} variant="hero" priority className="h-full w-full object-cover opacity-40" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline" style={{ color: '#C8A96B' }}>/ Factory Directory</span>
          <h1 className="mt-4 font-display tracking-tight" style={{ fontWeight: 600, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 1.02 }}>
            Verified factories <em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>worldwide.</em>
          </h1>
          <p className="mt-5 max-w-2xl" style={{ color: '#D9D9D9' }}>
            Filter {(allSuppliers?.length || 0)}+ manufacturers by country, supplier type, certification, MOQ, and trust score — or toggle on Manufacturers Only to exclude trading companies and agents.
          </p>
        </div>
      </section>

      {/* D-080 — search & filter controls moved beneath hero image */}
      <section className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-5 md:px-8 md:py-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-platinum-2" size={14} strokeWidth={1.5} />
              <input
                data-testid="factory-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search factories, specialties, countries…"
                className="giwahs-pill-field w-full py-3 pl-11 pr-4 text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setDrawer(true)} data-testid="factory-open-filters-mobile" className="btn-giwahs btn-giwahs-outline-gold lg:hidden">
                <SlidersHorizontal size={14} strokeWidth={1.5} /> Filters
              </button>
              <select
                data-testid="factory-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="btn-giwahs btn-giwahs-outline-gold giwahs-sort-select max-w-full"
              >
                <option value="trust-desc">Trust: High to Low</option>
                <option value="moq-asc">MOQ: Low to High</option>
                <option value="response">Response Rate</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-obsidian">
        <div className="mx-auto grid max-w-[1440px] gap-8 px-5 py-10 md:px-8 lg:grid-cols-[300px_1fr]">
          <div className="hidden lg:block">
            <FilterSidebar filters={filters} setFilters={setFilters} variant="supplier" />
          </div>
          <div>
            {/* Featured / Sponsored placements — separated from trust-sorted grid */}
            {(featured && featured.length > 0) && (
              <div data-testid="featured-suppliers-section" className="mb-10">
                <div className="flex flex-col gap-2 border-b border-hairline pb-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <div className="flex items-center gap-2 label-overline text-gold">
                      <Megaphone size={13} strokeWidth={1.6} /> Featured Suppliers
                    </div>
                    <p className="mt-2 max-w-2xl text-xs leading-relaxed text-platinum-2">
                      Sponsored placements — paid promotional listings. <strong className="text-platinum">Featured status does not affect trust score, verification, or default sort order below.</strong>
                    </p>
                  </div>
                  <span className="font-mono-data text-[11px] text-platinum-2">{featured.length} SPONSORED · ROTATED</span>
                </div>
                <div className="mt-5 grid gap-5 xl:grid-cols-2">
                  {featured.map((s, i) => <FeaturedSupplierCard key={s.id} supplier={s} index={i} />)}
                </div>
              </div>
            )}

            <div className="mb-5 flex items-center justify-between border-b border-hairline pb-3">
              <span className="font-mono-data text-xs text-platinum-2" data-testid="factory-result-count">
                {loading ? 'LOADING…' : `${results.length} FACTORIES`}
              </span>
              <span className="font-mono-data text-xs text-muted-warm">SORTED BY TRUST</span>
            </div>

            {loading && <LoadingState label="Loading factories…" />}
            {error && !loading && <ErrorState description={error?.message || 'Failed to load factories.'} onRetry={refetch} />}
            {!loading && !error && results.length === 0 && (
              <EmptyState title="No matching factories" description="Try widening your filters or clearing search." />
            )}
            {!loading && !error && results.length > 0 && (
              <div className="grid gap-6 md:grid-cols-2 2xl:grid-cols-3">
                {results.map((s, i) => <SupplierCard key={s.id} supplier={s} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>

      <MobileFilterDrawer open={drawer} onClose={() => setDrawer(false)}>
        <FilterSidebar filters={filters} setFilters={setFilters} variant="supplier" />
      </MobileFilterDrawer>
    </div>
  );
};

export default FactoryDirectory;
